import React, { useCallback } from 'react';
import { useShowContext } from 'framework/show';
import {
  calculateEnhancedTeamStructure,
  ProjectTeamType,
  TeamShowType,
  useProjectTeamService,
} from 'domain/shared/newProject';
import { FormFieldsData } from 'validation-schema-library/build/validation/types';
import { FormShowTemplate } from 'framework/form';
import StaffingTeamList from './StaffingTeamList';
import { EditableListFormShowTemplate } from 'framework/list';

const StaffingTeamWrapper = () => {
  const { data: teamData, setData } = useShowContext<TeamShowType>();
  const { createProjectTeam, updateProjectTeam, getProjectTeamValidationSchema } = useProjectTeamService();

  const enrichInputData = useCallback(
    (team: ProjectTeamType) => {
      const enhancedExpectedStructure = calculateEnhancedTeamStructure(
        team,
        teamData?.budget?.fte,
        teamData.budget?.billingType,
      );

      return {
        team: {
          ...(team || {}),
          expectedStructure: enhancedExpectedStructure.team?.expectedStructure || [],
        },
      };
    },
    [teamData?.budget?.billingType, teamData?.budget?.fte],
  );

  const getDataFunc = useCallback(async () => {
    return Promise.resolve(enrichInputData(teamData.team));
  }, [enrichInputData, teamData.team]);

  const submitFormFunc = async (formData: FormFieldsData) => {
    const requestBody = {
      id: formData.team.id,
      projectId: teamData.id,
      location: formData.team.location,
      type: formData.team.type,
      expectedStructure: formData.team.expectedStructure,
    };

    const submitFunc = formData.isNeedCreateTeam ? createProjectTeam : updateProjectTeam;
    return await submitFunc(requestBody).then(r => enrichInputData(r));
  };

  return (
    <FormShowTemplate
      isInitialsLoadingPosition
      getValidationSchemaFunc={getProjectTeamValidationSchema}
      submitFormFunc={submitFormFunc}
      afterSubmit={{
        message: 'The team have been successfully updated.',
        execute: async response => {
          setData?.({
            ...teamData,
            ...response,
          });
        },
      }}
      getDataFunc={getDataFunc}
    >
      <EditableListFormShowTemplate key={`key-${teamData.id}`} listSource="team.expectedStructure">
        <StaffingTeamList />
      </EditableListFormShowTemplate>
    </FormShowTemplate>
  );
};

export default StaffingTeamWrapper;
